import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import {
  listChannels, listChannelVideos, getTranscript, addChannel, indexVideo,
  indexBatchPreview, indexBatchStart,
  transcribeBatchPreview, transcribeBatchStart, cancelSaga,
  discoverChannels, discoverItems, listDiscoveryHistory, getDiscoveryRun, deleteDiscoveryRun,
  listCategoryVideos, setChannelCategory, listFollows, setFollow, markChannelSeen,
  tiktokLogin,
  expandSearchTerms,
} from '../services/transcriptsApi'

// TikTok sign-in (captcha-walled profiles): opens the controlled browser flow.
export function useExpandSearchTerms() {
  return useMutation({ mutationFn: expandSearchTerms })
}

export function useTiktokLogin() {
  return useMutation({ mutationFn: tiktokLogin })
}

export const useFollows = () =>
  useQuery({ queryKey: ['transcripts', 'follows'], queryFn: listFollows, staleTime: 60 * 1000 })

export function useSetFollow() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: setFollow,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transcripts', 'follows'] }),
  })
}

export function useMarkSeen() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: markChannelSeen,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transcripts', 'follows'] }),
  })
}

export const useDiscoverChannels = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: discoverChannels,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discover', 'history'] }),
  })
}
export const useDiscoverItems = () => useMutation({ mutationFn: discoverItems })
export const useDiscoveryHistory = () =>
  useQuery({ queryKey: ['discover', 'history'], queryFn: () => listDiscoveryHistory(20), staleTime: 30000 })
export const useDiscoveryRun = () => useMutation({ mutationFn: getDiscoveryRun })
export const useDeleteDiscoveryRun = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteDiscoveryRun,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discover', 'history'] }),
  })
}

export function useChannels() {
  return useQuery({ queryKey: ['transcripts', 'channels'], queryFn: listChannels, staleTime: 10 * 60 * 1000 })
}

// One channel (channelId) or every channel of a category (category) —
// multichannel rows carry channel/channel_title for cross-channel research.
// One options object rather than a positional list: with the length window this
// would have been nine positional arguments at the single call site.
export function useChannelVideos({ channelId, category, filter, limit, sort, search, offset = 0, minMinutes, maxMinutes, terms }) {
  return useQuery({
    queryKey: ['transcripts', 'videos', channelId || `cat:${category}`, filter, limit, sort, search, offset, minMinutes, maxMinutes, (terms || []).join('|')],
    queryFn: () =>
      channelId
        ? listChannelVideos({ channelId, filter, limit, offset, sort, search, minMinutes, maxMinutes, terms })
        : listCategoryVideos({ category, filter, limit, offset, sort, search, minMinutes, maxMinutes, terms }),
    enabled: !!(channelId || category),
    // Keep the current list on screen while a filter/limit change refetches.
    placeholderData: keepPreviousData,
  })
}

export function useSetChannelCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: setChannelCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transcripts', 'channels'] }),
  })
}

export const useIndexVideo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: indexVideo,
    // refresh the project's document list so "already indexed" rows flip
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['rag', 'documents', vars.project] })
      qc.invalidateQueries({ queryKey: ['rag', 'projects'] })
    },
  })
}

// Batch: preview the delta (transcribed − already-indexed) on demand.
export const useIndexBatchPreview = () => useMutation({ mutationFn: indexBatchPreview })

// Bulk transcription (plan Step 1): scope+delta preview, start, and stop.
export const useTranscribeBatchPreview = () => useMutation({ mutationFn: transcribeBatchPreview })
export const useTranscribeBatchStart = () => useMutation({ mutationFn: transcribeBatchStart })
export const useCancelSaga = () => useMutation({ mutationFn: cancelSaga })

// Batch: kick the durable saga (returns { saga_id } to poll via useSaga).
export const useIndexBatchStart = () => useMutation({ mutationFn: indexBatchStart })

export function useAddChannel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: addChannel,
    // also used to REFRESH an existing channel (incremental metadata merge) —
    // new videos must show up in the open list, not just the picker counts
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transcripts', 'channels'] })
      qc.invalidateQueries({ queryKey: ['transcripts', 'videos'] })
    },
  })
}

export function useTranscript(video, format) {
  return useQuery({
    queryKey: ['transcripts', 'transcript', video, format],
    queryFn: () => getTranscript({ video, format }),
    enabled: !!video,
    staleTime: 5 * 60 * 1000,
  })
}
